import {
  CardOrderProducts,
  CardOrderProductsCosts,
  CardOrderProductsSupplier,
  CardOrderProductValuesForm,
  CardOrderProductValuesFormValues,
  CardOrderSelectSupplier,
} from '@/components'
import { TabProductsProps } from './tab-products.type'

import { Button, EditIcon } from '@mercai/clever'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getFormCardsClasses,
  getFormClasses,
  getViewCardsClasses,
  getViewClasses,
} from './tab-products.constants'
import styles from './tab-products.module.css'

import {
  isEditOrderTracker,
  isSaleOrderEditable,
  onErrorMessage,
  onSuccessMessage,
} from '@/helpers'
import { useAuth } from '@/providers'
import {
  QUERIES_ADMIN,
  useOrderCenterSaleOrderUpdate,
  useSaleOrderItemUpdate,
} from '@/services'
import {
  LogisticShipmentProps,
  SaleOrderItemProps,
  SupplierProps,
} from '@/types'
import { useQueryClient } from '@tanstack/react-query'

export const TabProducts = ({ isLoading, saleOrder }: TabProductsProps) => {
  const { t } = useTranslation('screenOrderCenterPage')
  const [selectedProductIndex, setSelectedProductIndex] = useState<number>(0)
  const [editing, setEditing] = useState('')

  const { whoAmI } = useAuth()

  const queryClient = useQueryClient()

  const selectedProduct = useMemo(
    () => saleOrder?.items?.[selectedProductIndex],
    [selectedProductIndex, saleOrder],
  )

  const isEditable = isSaleOrderEditable(saleOrder?.status)

  const { mutateAsync, isPending: isLoadingUpdateOrder } =
    useOrderCenterSaleOrderUpdate(saleOrder?.uuid || '')
  const { mutateAsync: mutateAsyncUpdateItem, isPending: isLoadingUpdateItem } =
    useSaleOrderItemUpdate(selectedProduct?.uuid)

  const [productValues, setProductValues] = useState<
    Partial<CardOrderProductValuesFormValues>
  >({})

  const handleSelectedProductIndex = (index: number) => {
    setSelectedProductIndex(index)
    setEditing('')
  }

  const onCancelEdit = () => {
    setEditing('')
  }

  const onUpdate = (success: boolean) => {
    if (success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, saleOrder?.uuid],
      })

      onSuccessMessage(
        t('orderCenterShowTabProductsSuccessMessageTitle'),
        t('orderCenterShowTabProductsSuccessMessageDescription'),
      )

      setEditing('')
    } else {
      onErrorMessage(
        t('orderCenterShowTabProductsErrorMessageTitle'),
        t('orderCenterShowTabProductsErrorMessageDescription'),
      )
    }
  }

  const onEditProduct = async () => {
    if (!saleOrder?.items || !selectedProduct) return

    const response = await mutateAsyncUpdateItem(
      productValues as Partial<SaleOrderItemProps>,
    )

    onUpdate(response.success)
  }

  const onChangeCustomsCost = async (customsCost: string) => {
    if (!saleOrder?.shipments?.[0]) return

    const shipments: LogisticShipmentProps[] = [
      {
        uuid: saleOrder.shipments[0].uuid,
        customsCost,
      } as LogisticShipmentProps,
    ]

    const response = await mutateAsync({
      shipments,
    })

    return response
  }

  const onEditSupplier = async (
    supplier: Partial<SupplierProps>,
    supplierPaidAt: string,
  ) => {
    if (!saleOrder?.shipments?.[0]) return

    const shipments: LogisticShipmentProps[] = [
      {
        uuid: saleOrder.shipments[0].uuid,
        supplierUuid: supplier?.uuid || '',
        supplierPaidAt: supplierPaidAt || null,
      } as LogisticShipmentProps,
    ]

    const response = await mutateAsync({
      shipments,
    })

    onUpdate(response.success)
  }

  useEffect(() => {
    if (editing === '') {
      return setProductValues({})
    }

    if (editing === 'costs') {
      setProductValues({
        cost: selectedProduct?.cost || 0,
        price: +(selectedProduct?.price || '0'),
        quantity: selectedProduct?.quantity || 0,
        traderCommission: selectedProduct?.traderCommission || 0,
        package: selectedProduct?.package || '25kg_bags',
        uuid: selectedProduct?.uuid,
      })
    }
  }, [editing])

  return (
    <div className={styles.container}>
      <CardOrderProducts
        isLoading={isLoading}
        items={saleOrder?.items}
        selectedProduct={selectedProductIndex}
        setSelectedProduct={(index) => handleSelectedProductIndex(index)}
      />
      <div className={getViewClasses(!!editing)}>
        <div className={getViewCardsClasses(!!editing)}>
          <CardOrderProductsCosts
            isLoading={isLoading}
            item={selectedProduct}
            dollarQuotation={
              saleOrder?.payments?.[0]?.dollarExchangeRate || '0'
            }
            supplierName={saleOrder?.shipments?.[0]?.supplier?.name}
            actions={
              isEditOrderTracker(whoAmI?.email) &&
              isEditable && (
                <Button
                  className={styles.edit}
                  size="small"
                  variant="ghost"
                  onClick={() => setEditing('costs')}
                >
                  {t('orderCenterShowEditButton')}
                  <EditIcon />
                </Button>
              )
            }
          />
          <CardOrderProductsSupplier
            isLoading={isLoading}
            supplier={saleOrder?.shipments?.[0]?.supplier}
            supplierPaidAt={saleOrder?.shipments?.[0]?.supplierPaidAt}
            actions={
              isEditOrderTracker(whoAmI?.email) &&
              isEditable && (
                <Button
                  className={styles.edit}
                  size="small"
                  variant="ghost"
                  onClick={() => setEditing('supplier')}
                >
                  {t('orderCenterShowEditButton')}
                  <EditIcon />
                </Button>
              )
            }
          />
        </div>

        <div className={getFormCardsClasses(!!editing)}>
          <div className={getFormClasses(editing === 'costs')}>
            <CardOrderProductValuesForm
              values={productValues}
              onChange={setProductValues}
              onChangeCustomsCost={onChangeCustomsCost}
              customsCost={saleOrder?.shipments?.[0]?.customsCost || '0'}
              onSubmit={onEditProduct}
              onCancel={onCancelEdit}
              isLoading={isLoadingUpdateItem}
              saleOrder={saleOrder}
            />
          </div>
          <div className={getFormClasses(editing === 'supplier')}>
            <CardOrderSelectSupplier
              supplier={
                {
                  ...saleOrder?.shipments?.[0]?.supplier,
                } as SupplierProps
              }
              supplierPaidAt={saleOrder?.shipments?.[0]?.supplierPaidAt || ''}
              onCancel={onCancelEdit}
              onSubmit={onEditSupplier}
              isLoading={isLoadingUpdateOrder}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
